import { Component, computed, input, type OnInit, output, signal, viewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import type { DatePrecisionType } from "@pienso/ingest-types/corpus.interface";
import { Button, Radio, Select, Tag, TextInput } from "@ui";
import { COMMON_PATTERNS, formatSampleDate, generateDatePatterns, suggestDateFormats } from "./date-format-matcher";

export interface DateFormat {
  name: string; // user readable name
  value: string; // elastic search value
  pattern?: string; // the format string e.g. yyyy-MM-dd
  example?: string; // e.g. 2017-06-23
  desc?: string; // A textual description of the format
  url?: string; // link for more info
  precision: DatePrecisionType | null;
}

export const PREDEFINED_FORMATS: DateFormat[] = [
  {
    name: "Epoch Millis",
    value: "epoch_millis",
    pattern: undefined,
    example: "833824012784",
    desc: "A formatter for the number of milliseconds since the epoch. Note, that this timestamp is subject to the limits of a Java Long.MIN_VALUE and Long.MAX_VALUE",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Epoch Second",
    value: "epoch_second",
    pattern: undefined,
    example: "833824013",
    desc: "A formatter for the number of seconds since the epoch. Note, that this timestamp is subject to the limits of a Java Long.MIN_VALUE and Long. MAX_VALUE divided by 1000 (the number of milliseconds in a second)",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Date Optional Time",
    value: "date_optional_time",
    pattern: undefined,
    example: "96-6-3T13:46:52.784",
    desc: "A generic ISO datetime parser where the date is mandatory and the time is optional.",
    url: "http://www.joda.org/joda-time/apidocs/org/joda/time/format/ISODateTimeFormat.html#dateOptionalTimeParser--",
    precision: "day",
  },
  {
    name: "Strict Date Optional Time",
    value: "strict_date_optional_time",
    pattern: undefined,
    example: "1996-06-03T13:46:52.784",
    desc: "A generic ISO datetime parser where the date is mandatory and the time is optional.",
    url: "http://www.joda.org/joda-time/apidocs/org/joda/time/format/ISODateTimeFormat.html#dateOptionalTimeParser--",
    precision: "day",
  },
  {
    name: "Basic Date",
    value: "basic_date",
    pattern: "yyyyMMdd",
    example: "19960603",
    desc: "A basic formatter for a full date as four digit year, two digit month of year, and two digit day of month",
    url: undefined,
    precision: "day",
  },
  {
    name: "Basic Date Time",
    value: "basic_date_time",
    pattern: "yyyyMMdd'T'HHmmss.SSSZ",
    example: "19960603T134652.784-0800",
    desc: "A basic formatter that combines a basic date and time, separated by a T",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Basic Date Time No Millis",
    value: "basic_date_time_no_millis",
    pattern: "yyyyMMdd'T'HHmmssZ",
    example: "19960603T134652-0800",
    desc: "A basic formatter that combines a basic date and time without millis, separated by a T",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Basic Ordinal Date",
    value: "basic_ordinal_date",
    pattern: "yyyyDDD",
    example: "1996154",
    desc: "A formatter for a full ordinal date, using a four digit year and three digit dayOfYear",
    url: undefined,
    precision: "day",
  },
  {
    name: "Basic Ordinal Date Time",
    value: "basic_ordinal_date_time",
    pattern: "yyyyDDD'T'HHmmss.SSSZ",
    example: "1996154T134652.784-0800",
    desc: "A formatter for a full ordinal date and time, using a four digit year and three digit dayOfYear",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Basic Ordinal Date Time No Millis",
    value: "basic_ordinal_date_time_no_millis",
    pattern: "yyyyDDD'T'HHmmssZ",
    example: "1996154T134652-0800",
    desc: "A formatter for a full ordinal date and time without millis, using a four digit year and three digit dayOfYear",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Basic Week Date",
    value: "basic_week_date",
    pattern: "xxxx'W'wwe",
    example: "1996W257",
    desc: "A basic formatter for a full date as four digit weekyear, two digit week of weekyear, and one digit day of week",
    url: undefined,
    precision: "week",
  },
  {
    name: "Strict Basic Week Date",
    value: "strict_basic_week_date",
    pattern: "xxxx'W'wwe",
    example: "1996W257",
    desc: "A basic formatter for a full date as four digit weekyear, two digit week of weekyear, and one digit day of week",
    url: undefined,
    precision: "day",
  },
  {
    name: "Basic Week Date Time",
    value: "basic_week_date_time",
    pattern: "xxxx'W'wwe'T'HHmmss.SSSZ",
    example: "1996W257T134652.784-0800",
    desc: "A basic formatter that combines a basic weekyear date and time, separated by a T",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Strict Basic Week Date Time",
    value: "strict_basic_week_date_time",
    pattern: "xxxx'W'wwe'T'HHmmss.SSSZ",
    example: "1996W257T134652.784-0800",
    desc: "A basic formatter that combines a basic weekyear date and time, separated by a T",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Basic Week Date Time No Millis",
    value: "basic_week_date_time_no_millis",
    pattern: "xxxx'W'wwe'T'HHmmssZ",
    example: "1996W257T134652-0800",
    desc: "A basic formatter that combines a basic weekyear date and time without millis, separated by a T",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Strict Basic Week Date Time No Millis",
    value: "strict_basic_week_date_time_no_millis",
    pattern: "xxxx'W'wwe'T'HHmmssZ",
    example: "1996W257T134652-0800",
    desc: "A basic formatter that combines a basic weekyear date and time without millis, separated by a T",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Date",
    value: "date",
    pattern: "yyyy-MM-dd",
    example: "96-6-3",
    desc: "A formatter for a full date as four digit year, two digit month of year, and two digit day of month",
    url: undefined,
    precision: "day",
  },
  {
    name: "Strict Date",
    value: "strict_date",
    pattern: "yyyy-MM-dd",
    example: "1996-06-03",
    desc: "A formatter for a full date as four digit year, two digit month of year, and two digit day of month",
    url: undefined,
    precision: "day",
  },
  {
    name: "Date Hour",
    value: "date_hour",
    pattern: "yyyy-MM-dd'T'HH",
    example: "96-6-3T13",
    desc: "A formatter that combines a full date and two digit hour of day",
    url: undefined,
    precision: "hour",
  },
  {
    name: "Strict Date Hour",
    value: "strict_date_hour",
    pattern: "yyyy-MM-dd'T'HH",
    example: "1996-06-03T13",
    desc: "A formatter that combines a full date and two digit hour of day",
    url: undefined,
    precision: "hour",
  },
  {
    name: "Date Hour Minute",
    value: "date_hour_minute",
    pattern: "yyyy-MM-dd'T'HH:mm",
    example: "96-6-3T13:46",
    desc: "A formatter that combines a full date, two digit hour of day, and two digit minute of hour",
    url: undefined,
    precision: "minute",
  },
  {
    name: "Strict Date Hour Minute",
    value: "strict_date_hour_minute",
    pattern: "yyyy-MM-dd'T'HH:mm",
    example: "1996-06-03T13:46",
    desc: "A formatter that combines a full date, two digit hour of day, and two digit minute of hour",
    url: undefined,
    precision: "minute",
  },
  {
    name: "Date Hour Minute Second",
    value: "date_hour_minute_second",
    pattern: "yyyy-MM-dd'T'HH:mm:ss",
    example: "96-6-3T13:46:52",
    desc: "A formatter that combines a full date, two digit hour of day, two digit minute of hour, and two digit second of minute",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Strict Date Hour Minute Second",
    value: "strict_date_hour_minute_second",
    pattern: "yyyy-MM-dd'T'HH:mm:ss",
    example: "1996-06-03T13:46:52",
    desc: "A formatter that combines a full date, two digit hour of day, two digit minute of hour, and two digit second of minute",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Date Hour Minute Second Fraction",
    value: "date_hour_minute_second_fraction",
    pattern: "yyyy-MM-dd'T'HH:mm:ss.SSS",
    example: "96-6-3T13:46:52.784",
    desc: "A formatter that combines a full date, two digit hour of day, two digit minute of hour, two digit second of minute, and three digit fraction of second",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Strict Date Hour Minute Second Fraction",
    value: "strict_date_hour_minute_second_fraction",
    pattern: "yyyy-MM-dd'T'HH:mm:ss.SSS",
    example: "1996-06-03T13:46:52.784",
    desc: "A formatter that combines a full date, two digit hour of day, two digit minute of hour, two digit second of minute, and three digit fraction of second",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Date Hour Minute Second Millis",
    value: "date_hour_minute_second_millis",
    pattern: "yyyy-MM-dd'T'HH:mm:ss.SSS",
    example: "96-6-3T13:46:52.784",
    desc: "A formatter that combines a full date, two digit hour of day, two digit minute of hour, two digit second of minute, and three digit fraction of second",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Strict Date Hour Minute Second Millis",
    value: "strict_date_hour_minute_second_millis",
    pattern: "yyyy-MM-dd'T'HH:mm:ss.SSS",
    example: "1996-06-03T13:46:52.784",
    desc: "A formatter that combines a full date, two digit hour of day, two digit minute of hour, two digit second of minute, and three digit fraction of second",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Date Time",
    value: "date_time",
    pattern: "yyyy-MM-dd'T'HH:mm:ss.SSSZZ",
    example: "96-6-3T13:46:52.784-08:00",
    desc: "A formatter that combines a full date and time, separated by a T",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Strict Date Time",
    value: "strict_date_time",
    pattern: "yyyy-MM-dd'T'HH:mm:ss.SSSZZ",
    example: "1996-06-03T13:46:52.784-08:00",
    desc: "A formatter that combines a full date and time, separated by a T",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Date Time No Millis",
    value: "date_time_no_millis",
    pattern: "yyyy-MM-dd'T'HH:mm:ssZZ",
    example: "96-6-3T13:46:52-08:00",
    desc: "A formatter that combines a full date and time without millis, separated by a T",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Strict Date Time No Millis",
    value: "strict_date_time_no_millis",
    pattern: "yyyy-MM-dd'T'HH:mm:ssZZ",
    example: "1996-06-03T13:46:52-08:00",
    desc: "A formatter that combines a full date and time without millis, separated by a T",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Ordinal Date",
    value: "ordinal_date",
    pattern: "yyyy-DDD",
    example: "96-154",
    desc: "A formatter for a full ordinal date, using a four digit year and three digit dayOfYear",
    url: undefined,
    precision: "day",
  },
  {
    name: "Strict Ordinal Date",
    value: "strict_ordinal_date",
    pattern: "yyyy-DDD",
    example: "1996-154",
    desc: "A formatter for a full ordinal date, using a four digit year and three digit dayOfYear",
    url: undefined,
    precision: "day",
  },
  {
    name: "Ordinal Date Time",
    value: "ordinal_date_time",
    pattern: "yyyy-DDD'T'HH:mm:ss.SSSZZ",
    example: "96-154T13:46:52.784-08:00",
    desc: "A formatter for a full ordinal date and time, using a four digit year and three digit dayOfYear",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Strict Ordinal Date Time",
    value: "strict_ordinal_date_time",
    pattern: "yyyy-DDD'T'HH:mm:ss.SSSZZ",
    example: "1996-154T13:46:52.784-08:00",
    desc: "A formatter for a full ordinal date and time, using a four digit year and three digit dayOfYear",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Ordinal Date Time No Millis",
    value: "ordinal_date_time_no_millis",
    pattern: "yyyy-DDD'T'HH:mm:ssZZ",
    example: "96-154T13:46:52-08:00",
    desc: "A formatter for a full ordinal date and time without millis, using a four digit year and three digit dayOfYear",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Strict Ordinal Date Time No Millis",
    value: "strict_ordinal_date_time_no_millis",
    pattern: "yyyy-DDD'T'HH:mm:ssZZ",
    example: "1996-154T13:46:52-08:00",
    desc: "A formatter for a full ordinal date and time without millis, using a four digit year and three digit dayOfYear",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Week Date",
    value: "week_date",
    pattern: "xxxx-'W'ww-e",
    example: "1996-W25-7",
    desc: "A formatter for a full date as four digit weekyear, two digit week of weekyear, and one digit day of week",
    url: undefined,
    precision: "day",
  },
  {
    name: "Strict Week Date",
    value: "strict_week_date",
    pattern: "xxxx-'W'ww-e",
    example: "1996-W25-7",
    desc: "A formatter for a full date as four digit weekyear, two digit week of weekyear, and one digit day of week",
    url: undefined,
    precision: "day",
  },
  {
    name: "Week Date Time",
    value: "week_date_time",
    pattern: "xxxx-'W'ww-e'T'HH:mm:ss.SSSZZ",
    example: "1996-W25-7T13:46:52.784-08:00",
    desc: "A formatter that combines a full weekyear date and time, separated by a T",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Strict Week Date Time",
    value: "strict_week_date_time",
    pattern: "xxxx-'W'ww-e'T'HH:mm:ss.SSSZZ",
    example: "1996-W25-7T13:46:52.784-08:00",
    desc: "A formatter that combines a full weekyear date and time, separated by a T",
    url: undefined,
    precision: "milliseconds",
  },
  {
    name: "Week Date Time No Millis",
    value: "week_date_time_no_millis",
    pattern: "xxxx-'W'ww-e'T'HH:mm:ssZZ",
    example: "1996-W25-7T13:46:52-08:00",
    desc: "A formatter that combines a full weekyear date and time without millis, separated by a T",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Strict Week Date Time No Millis",
    value: "strict_week_date_time_no_millis",
    pattern: "xxxx-'W'ww-e'T'HH:mm:ssZZ",
    example: "1996-W25-7T13:46:52-08:00",
    desc: "A formatter that combines a full weekyear date and time without millis, separated by a T",
    url: undefined,
    precision: "seconds",
  },
  {
    name: "Weekyear",
    value: "weekyear",
    pattern: "xxxx",
    example: "1996",
    desc: "A formatter for a four digit weekyear",
    url: undefined,
    precision: "year",
  },
  {
    name: "Strict Weekyear",
    value: "strict_weekyear",
    pattern: "xxxx",
    example: "1996",
    desc: "A formatter for a four digit weekyear",
    url: undefined,
    precision: "year",
  },
  {
    name: "Weekyear Week",
    value: "weekyear_week",
    pattern: "xxxx-'W'ww",
    example: "1996-W25",
    desc: "A formatter for a four digit weekyear and two digit week of weekyear",
    url: undefined,
    precision: "week",
  },
  {
    name: "Strict Weekyear Week",
    value: "strict_weekyear_week",
    pattern: "xxxx-'W'ww",
    example: "1996-W25",
    desc: "A formatter for a four digit weekyear and two digit week of weekyear",
    url: undefined,
    precision: "week",
  },
  {
    name: "Weekyear Week Day",
    value: "weekyear_week_day",
    pattern: "xxxx-'W'ww-e",
    example: "1996-W25-7",
    desc: "A formatter for a four digit weekyear, two digit week of weekyear, and one digit day of week",
    url: undefined,
    precision: "day",
  },
  {
    name: "Strict Weekyear Week Day",
    value: "strict_weekyear_week_day",
    pattern: "xxxx-'W'ww-e",
    example: "1996-W25-7",
    desc: "A formatter for a four digit weekyear, two digit week of weekyear, and one digit day of week",
    url: undefined,
    precision: "day",
  },
  {
    name: "Year",
    value: "year",
    pattern: "yyyy",
    example: "96",
    desc: "A formatter for a four digit year",
    url: undefined,
    precision: "year",
  },
  {
    name: "Strict Year",
    value: "strict_year",
    pattern: "yyyy",
    example: "1996",
    desc: "A formatter for a four digit year",
    url: undefined,
    precision: "year",
  },
  {
    name: "Year Month",
    value: "year_month",
    pattern: "yyyy-MM",
    example: "96-6",
    desc: "A formatter for a four digit year and two digit month of year",
    url: undefined,
    precision: "month",
  },
  {
    name: "Strict Year Month",
    value: "strict_year_month",
    pattern: "yyyy-MM",
    example: "1996-06",
    desc: "A formatter for a four digit year and two digit month of year",
    url: undefined,
    precision: "month",
  },
  {
    name: "Year Month Day",
    value: "year_month_day",
    pattern: "yyyy-MM-dd",
    example: "96-6-3",
    desc: "A formatter for a four digit year, two digit month of year, and two digit day of month",
    url: undefined,
    precision: "day",
  },
  {
    name: "Strict Year Month Day",
    value: "strict_year_month_day",
    pattern: "yyyy-MM-dd",
    example: "1996-06-03",
    desc: "A formatter for a four digit year, two digit month of year, and two digit day of month",
    url: undefined,
    precision: "day",
  },
];

function patternToFormat(pattern: string): DateFormat {
  return { name: pattern, value: pattern, pattern, precision: parseDateFormatToPrecision(pattern) };
}

// Patterns Elasticsearch's built-ins do not cover; the Joda pattern doubles as the value.
const COMMON_FORMATS: DateFormat[] = COMMON_PATTERNS.map(patternToFormat);

// Only offered as suggestions when they match the data; too many to list.
const GENERATED_FORMATS: DateFormat[] = generateDatePatterns().map(patternToFormat);

export function parseDateFormatToPrecision(format: string): DatePrecisionType {
  const found_format = PREDEFINED_FORMATS.find((item) => item.value === format);
  if (found_format) return found_format.precision!;
  const precisionMap: { [pre in DatePrecisionType]: boolean } = {} as { [pre in DatePrecisionType]: boolean };
  let ignore = false;
  for (let i = 0; i < format.length; ++i) {
    if (format[i] === "'" || format[i] === "[" || format[i] === "]") {
      ignore = !ignore;
    }
    if (ignore) continue;
    if (format[i] === "y" || format[i] === "u" || format[i] === "Y") precisionMap["year"] = true;
    else if (format[i] === "M" || format[i] === "L") precisionMap["month"] = true;
    else if (format[i] === "w" || format[i] === "W" || format[i] === "F") precisionMap["week"] = true;
    else if (format[i] === "d" || format[i] === "D" || format[i] === "E" || format[i] === "e" || format[i] === "c")
      precisionMap["day"] = true;
    else if (format[i] === "h" || format[i] === "H" || format[i] === "K" || format[i] === "k")
      precisionMap["hour"] = true;
    else if (format[i] === "m") precisionMap["minute"] = true;
    else if (format[i] === "s") precisionMap["seconds"] = true;
    else if (format[i] === "S" || format[i] === "A" || format[i] === "n" || format[i] === "N")
      precisionMap["milliseconds"] = true;
  }
  let precision: DatePrecisionType = "year";
  if (!precisionMap["year"]) return precision;
  if (precisionMap["month"]) precision = "month";
  if (precisionMap["week"]) precision = "week";
  if (precisionMap["day"]) precision = "day";
  if (precision === "year") return precision;
  if (precisionMap["hour"]) precision = "hour";
  else return precision;
  if (precisionMap["minute"]) precision = "minute";
  else return precision;
  if (precisionMap["seconds"]) precision = "seconds";
  else return precision;
  if (precisionMap["milliseconds"]) precision = "milliseconds";
  return precision;
}

@Component({
  selector: "app-date-format",
  templateUrl: "./date-format.component.html",
  styleUrls: ["./date-format.component.scss"],
  imports: [FormsModule, Button, Radio, Select, Tag, TextInput],
})
export class DateFormatComponent implements OnInit {
  predefinedFormats = PREDEFINED_FORMATS;
  private readonly knownFormats = [...COMMON_FORMATS, ...PREDEFINED_FORMATS];

  // Appended to the predefined list as a selectable row; picking it reveals the custom format input.
  readonly customOption: DateFormat = {
    name: "Custom format",
    value: "__custom__",
    precision: null,
  };

  readonly samples = input<unknown[]>([]);

  readonly suggestions = computed(() =>
    suggestDateFormats(this.samples(), [...this.knownFormats, ...GENERATED_FORMATS]),
  );
  private readonly suggestionByValue = computed(
    () => new Map(this.suggestions().map((suggestion) => [suggestion.format.value, suggestion])),
  );
  // Suggestions are deduped by pattern, so "others" must be too (e.g. strict_date vs yyyy-MM-dd)
  private readonly suggestedKeys = computed(
    () => new Set(this.suggestions().map((suggestion) => suggestion.format.pattern ?? suggestion.format.value)),
  );

  private readonly otherFormats = computed(() => {
    const saved = this.listSelection;
    // Kept even when a same-pattern twin is suggested, otherwise the select has no matching option
    const isSavedHidden = saved !== null && !this.suggestionByValue().has(saved.value);
    const others = this.knownFormats.filter(
      (format) => !this.suggestedKeys().has(format.pattern ?? format.value) || (isSavedHidden && format === saved),
    );
    // A saved generated pattern isn't in knownFormats; keep it listed so the select can show it
    if (saved && isSavedHidden && !this.knownFormats.includes(saved)) {
      others.push(saved);
    }
    return others;
  });

  // Suggested formats are moved to the top so each value appears once in the list.
  // Formats that parse none of the samples stay hidden until the user asks for them.
  readonly showOtherFormats = signal(false);
  readonly areOthersHidden = computed(() => this.suggestions().length > 0 && !this.showOtherFormats());

  readonly selectOptions = computed<DateFormat[]>(() => {
    const suggested = this.suggestions().map((suggestion) => suggestion.format);
    if (this.areOthersHidden()) {
      return [...suggested, this.customOption];
    }
    return [...suggested, ...this.otherFormats(), this.customOption];
  });

  readonly firstOtherOption = computed(() => (this.areOthersHidden() ? undefined : this.otherFormats()[0]));
  readonly hiddenFormatCount = computed(() => this.otherFormats().length);

  formatMode = "predefined";

  listSelection: DateFormat | null = null; // track the currently selected item in the list

  customFormat: DateFormat = {
    name: "custom",
    value: "",
    example: "MM/dd/yyyy",
    precision: null,
  };

  readonly initialValue = input<string | undefined>(undefined);
  readonly valueChanged = output<DateFormat | null>();
  readonly dateFormatSelect = viewChild.required<Select<DateFormat>>("dateFormatSelect");

  ngOnInit() {
    const initialValue = this.initialValue();
    if (!initialValue) {
      this.formatMode = "predefined";
      return;
    }
    const found = [...this.knownFormats, ...GENERATED_FORMATS].find((fmt) => fmt.value === initialValue);
    if (found) {
      this.formatMode = "predefined";
      this.listSelection = found;
      if (!this.suggestionByValue().has(found.value)) {
        this.showOtherFormats.set(true); // the saved format is listed under "Other formats"
      }
    } else {
      this.formatMode = "custom";
      this.customFormat.value = initialValue;
    }
  }

  selectedOption(): DateFormat | null {
    if (this.formatMode === "custom") {
      return this.customOption;
    }
    return this.listSelection;
  }

  onOptionChange(option: DateFormat | null): void {
    if (!option) {
      return;
    }
    if (option === this.customOption) {
      this.formatMode = "custom";
      // the custom input grows the last row's height; scroll it into view so it isn't clipped by the dropdown
      setTimeout(() => {
        document.querySelector(".date-format-option__custom-input")?.scrollIntoView({ block: "end" });
      });
    } else {
      this.formatMode = "predefined";
      this.listSelection = option;
      this.customFormat.value = "";
      this.dateFormatSelect().closeDropdown();
    }
    this.emitFormat();
  }

  isCustom(): boolean {
    return this.formatMode === "custom";
  }

  suggestionFor(option: DateFormat) {
    return this.suggestionByValue().get(option.value);
  }

  samplePreview(option: DateFormat): string | null {
    const suggestion = this.suggestionFor(option);
    return suggestion ? `${suggestion.sampleRaw} → ${formatSampleDate(suggestion.sampleParsed)}` : null;
  }

  isFirstSuggestion(option: DateFormat): boolean {
    return this.suggestions()[0]?.format === option;
  }

  onCustomFormatChange(value: string): void {
    this.customFormat.value = value;
    this.emitFormat();
  }

  selectedFormat(): DateFormat | null {
    if (this.formatMode === "predefined" && this.listSelection) {
      return this.listSelection;
    } else if (this.formatMode === "custom" && this.customFormat.value) {
      return this.customFormat;
    }
    return null;
  }

  hasFormat() {
    return !!this.selectedFormat();
  }

  emitFormat() {
    this.valueChanged.emit(this.selectedFormat());
  }
}
